import { ref } from 'vue'

interface Notification {
  id: number
  message: string
  type: 'success' | 'error' | 'info' | 'warning'
  duration?: number
}

// Shared state - singleton pattern
const notifications = ref<Notification[]>([])
let notificationId = 0

export function useNotifications() {
  // Return the same shared state instance
  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'info' | 'warning' = 'info',
    duration: number = 5000,
  ) => {
    const id = notificationId++
    const notification: Notification = {
      id,
      message,
      type,
      duration,
    }

    notifications.value.push(notification)

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id)
      }, duration)
    }

    return id
  }

  const removeNotification = (id: number) => {
    const index = notifications.value.findIndex((n) => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }

  const showSuccess = (message: string, duration?: number) => {
    return showNotification(message, 'success', duration)
  }

  const showError = (message: string, duration?: number) => {
    return showNotification(message, 'error', duration)
  }

  const showInfo = (message: string, duration?: number) => {
    return showNotification(message, 'info', duration)
  }

  const showWarning = (message: string, duration?: number) => {
    return showNotification(message, 'warning', duration)
  }

  return {
    notifications,
    showNotification,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    removeNotification,
  }
}
